import React, { useState, memo } from 'react'
import classNames from 'classnames'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Snackbar } from 'transfix-ui/core'
import { CloseIcon } from 'transfix-icons'
import 'scss/5_components/_toasts.scss'

export type ToastVariation = 'default' | 'alert'

export type ToastState = {
  toastOpen: boolean
  toastMessage?: string | React.ReactNode
  toastType?: ToastVariation
  multiLine?: boolean
  onClose?: () => void
}

export type TToastNewState = Omit<ToastState, 'toastOpen'>

/**
 * This hook should contain all of the necessary state and update functions for
 * working with a Toast component anywhere.
 */
export const useToastState = () => {
  const [toastState, setToastState] = useState<ToastState>({
    toastOpen: false,
  })

  const openToast = (newToastState: TToastNewState) => {
    setToastState({ ...newToastState, toastOpen: true })
  }

  const closeToast = () => {
    if (toastState.onClose) {
      toastState.onClose()
    }

    setToastState({ toastOpen: false })
  }

  return { ...toastState, openToast, closeToast }
}

interface IToastProps {
  /**
   * Message to display to the user
   *
   */
  message?: React.ReactNode | string
  /**
   * Whether the toast message is visible
   *
   */
  open: boolean
  /**
   * Called when the modal is closed bu the user
   *
   */
  onClose: () => void
  /**
   * Style of the toast message
   *
   * @default 'default'
   */
  variation?: ToastVariation
  /**
   * Time before the toast message auto-hides
   *
   * @default 5000
   */
  duration?: number | null
  /**
   * Whether to split long toast messages into multiple lines
   *
   */
  multiLine?: boolean
}

export const Toast = memo<IToastProps>(
  ({ duration = 5000, message, multiLine, onClose, open, variation: style = 'default' }) => {
    const classes = classNames('dpl-toast', `dpl-toast-${style}`, {
      'dpl-toast-no-wrap': !multiLine,
    })
    return (
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        data-test='toast-alert'
        classes={{ root: classes }}
        open={open}
        autoHideDuration={duration}
        onClose={onClose}
        message={message}
        action={
          <IconButton
            color='tertiary'
            Icon={CloseIcon}
            onClick={onClose}
            label='Close'
            size='large'
          />
        }
      />
    )
  }
)

export default Toast
